/**
 * Entrevistas levantadas, con filtros de revisión.
 *
 * El filtro que más se usa es «GPS fuera de polígono»: es la cola de trabajo
 * del supervisor para revisar las capturas que el encuestador justificó en
 * campo, que es el otro extremo de la decisión de advertir en lugar de
 * bloquear.
 */

import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  api,
  ApiError,
  type InterviewDetail,
  type TranscriptAnswer,
} from '../api/client';
import { useAdmin } from '../store';
import { StatusChip, Tile, fmtDecimal, fmtInt } from '../components/charts';
import { LazyCaptureMap } from '../components/LazyCaptureMap';

export function InterviewsPage() {
  const { can } = useAdmin();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    gpsOutside: false,
    hasFlags: false,
    isPilot: '',
  });
  const [showMap, setShowMap] = useState(false);
  /** Entrevista abierta en el panel de detalle. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const [annulling, setAnnulling] = useState<string | null>(null);
  const [annulReason, setAnnulReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const detalle = useQuery({
    queryKey: ['interview', abierta],
    queryFn: () => api.interview(abierta!),
    enabled: abierta !== null,
  });

  const interviews = useQuery({
    queryKey: ['interviews', filters],
    queryFn: () =>
      api.interviews({
        take: '100',
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.gpsOutside ? { gpsOutside: 'true' } : {}),
        ...(filters.hasFlags ? { hasFlags: 'true' } : {}),
        ...(filters.isPilot ? { isPilot: filters.isPilot } : {}),
      }),
  });

  const captureMap = useQuery({
    queryKey: ['monitoring', 'capture-map', filters.gpsOutside],
    queryFn: () => api.captureMap(filters.gpsOutside ? { outsideOnly: 'true' } : {}),
    enabled: showMap,
  });

  const annul = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.requestAnnulment(id, reason),
    onSuccess: () => {
      setAnnulling(null);
      setAnnulReason('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['quality'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo solicitar.'),
  });

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Entrevistas</h1>
        <p className="page-sub">
          Toca el folio para leer las respuestas de una entrevista. Los datos de contacto de Q1 y Q2
          van cifrados aparte: se consultan a petición, con permiso, y cada consulta queda en la
          bitácora.
        </p>
      </div>

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="status">
            Estatus
          </label>
          <select
            id="status"
            className="select"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">Todos</option>
            <option value="completada">Completada</option>
            <option value="parcial">Parcial</option>
            <option value="en_progreso">En progreso</option>
            <option value="anulada">Anulada</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="pilot">
            Piloto
          </label>
          <select
            id="pilot"
            className="select"
            value={filters.isPilot}
            onChange={(e) => setFilters((f) => ({ ...f, isPilot: e.target.value }))}
          >
            <option value="">Todas</option>
            <option value="false">Excluir piloto</option>
            <option value="true">Sólo piloto</option>
          </select>
        </div>
        <label className="row" style={{ fontSize: 14, paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.gpsOutside}
            onChange={(e) => setFilters((f) => ({ ...f, gpsOutside: e.target.checked }))}
          />
          GPS fuera de polígono
        </label>
        <label className="row" style={{ fontSize: 14, paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={filters.hasFlags}
            onChange={(e) => setFilters((f) => ({ ...f, hasFlags: e.target.checked }))}
          />
          Con alertas de calidad
        </label>
      </div>

      {error ? <div className="callout callout--critical">{error}</div> : null}

      {/* Mapa de capturas: muestra los patrones que una tabla no revela, como
          un recorrido imposible entre entrevistas consecutivas. */}
      {abierta ? (
        <InterviewDetailPanel
          query={detalle}
          onClose={() => setAbierta(null)}
          canReadPii={can('pii:read')}
          canRelocate={can('interviews:relocate')}
        />
      ) : null}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              Mapa de capturas <span className="ref">§9.4</span>
            </h2>
            <p className="panel__note">
              Dónde se levantó cada entrevista y cuáles cayeron fuera de su polígono. No incluye
              ningún dato del entrevistado.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setShowMap((v) => !v)}
          >
            {showMap ? 'Ocultar mapa' : 'Mostrar mapa'}
          </button>
        </div>

        {showMap ? (
          captureMap.data ? (
            <LazyCaptureMap data={captureMap.data} />
          ) : (
            <div className="spinner" />
          )
        ) : null}
      </div>

      {interviews.data ? (
        <>
          <div className="grid grid--3">
            <Tile label="Resultados del filtro" value={fmtInt(interviews.data.total)} />
            <Tile
              label="Con alertas"
              value={fmtInt(interviews.data.items.filter((i) => i.qualityFlags.length > 0).length)}
              meta="En la página visible"
            />
            <Tile
              label="GPS fuera de polígono"
              value={fmtInt(
                interviews.data.items.filter(
                  (i) =>
                    i.gpsStatus === 'inside-other-section' ||
                    i.gpsStatus === 'outside-all-sections',
                ).length,
              )}
              meta="En la página visible"
            />
          </div>

          <div className="table-wrap scroll-y">
            <table className="table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Conglomerado</th>
                  <th>Encuestador</th>
                  <th>Fecha</th>
                  <th>Duración</th>
                  <th>GPS</th>
                  <th>Alertas</th>
                  <th>Celda X1</th>
                  <th>Estatus</th>
                  {can('interviews:annul') ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {interviews.data.items.map((interview) => (
                  <tr key={interview.id}>
                    <td>
                      <button
                        type="button"
                        className="btn btn--link mono"
                        onClick={() =>
                          setAbierta((actual) => (actual === interview.id ? null : interview.id))
                        }
                        title="Ver las respuestas de esta entrevista"
                      >
                        {interview.clientUuid.slice(0, 8)}
                      </button>
                    </td>
                    <td>
                      <div className="strong">{interview.cluster.id}</div>
                      <div className="faint">
                        Sec. {interview.cluster.seccion} · est. {interview.cluster.stratumId}
                      </div>
                    </td>
                    <td>
                      <div>{interview.encuestador.code}</div>
                      <div className="faint">{interview.encuestador.name}</div>
                    </td>
                    <td className="faint">
                      {new Date(interview.startedAt).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="num">
                      {interview.durationMinutes !== null ? (
                        <span
                          className={interview.durationMinutes < 10 ? 'strong' : ''}
                          style={
                            interview.durationMinutes < 10
                              ? { color: 'var(--status-critical)' }
                              : undefined
                          }
                        >
                          {fmtDecimal(interview.durationMinutes)} min
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <GpsCell interview={interview} />
                    </td>
                    <td>
                      {interview.qualityFlags.length === 0 ? (
                        <span className="faint">—</span>
                      ) : (
                        <div className="row row--wrap" style={{ gap: 4 }}>
                          {interview.qualityFlags.map((flag) => (
                            <span key={flag} className="chip" title={flag}>
                              {flag.split('-').slice(0, 2).join(' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="mono faint">{interview.experimentCellId ?? '—'}</td>
                    <td>
                      <StatusChip
                        status={
                          interview.status === 'completada'
                            ? 'good'
                            : interview.status === 'anulada'
                              ? 'critical'
                              : 'warning'
                        }
                      >
                        {interview.status}
                      </StatusChip>
                      {interview.isPilot ? (
                        <div>
                          <span className="chip">piloto</span>
                        </div>
                      ) : null}
                      {interview.annulment ? (
                        <div className="faint">anulación: {interview.annulment.status}</div>
                      ) : null}
                    </td>
                    {can('interviews:annul') ? (
                      <td>
                        {interview.annulment || interview.status === 'anulada' ? null : annulling ===
                          interview.id ? (
                          <div className="stack" style={{ minWidth: 200 }}>
                            <input
                              className="input"
                              placeholder="Motivo de la anulación"
                              value={annulReason}
                              onChange={(e) => setAnnulReason(e.target.value)}
                            />
                            <div className="row">
                              <button
                                type="button"
                                className="btn btn--danger btn--sm"
                                disabled={annulReason.trim().length < 5 || annul.isPending}
                                onClick={() =>
                                  annul.mutate({
                                    id: interview.id,
                                    reason: annulReason.trim(),
                                  })
                                }
                              >
                                Solicitar
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => {
                                  setAnnulling(null);
                                  setAnnulReason('');
                                }}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => setAnnulling(interview.id)}
                          >
                            Anular
                          </button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {interviews.data.items.length === 0 ? (
            <p className="muted">No hay entrevistas con el filtro actual.</p>
          ) : null}
        </>
      ) : (
        <div className="spinner" />
      )}
    </>
  );
}

/** Estado del GPS con su distancia y el motivo que dio el encuestador. */
function GpsCell({
  interview,
}: {
  interview: {
    gpsStatus: string | null;
    distanceToExpectedMeters: number | null;
    outOfPolygonReason: string | null;
    expectedFrameSeccion: number | null;
    detectedFrameSeccion: number | null;
  };
}) {
  if (!interview.gpsStatus) return <span className="faint">—</span>;

  const isValid =
    interview.gpsStatus === 'inside-expected' ||
    interview.gpsStatus === 'inside-expected-by-equivalence' ||
    interview.gpsStatus === 'inside-buffer';

  const label: Record<string, string> = {
    'inside-expected': 'Dentro',
    'inside-expected-by-equivalence': 'Dentro (equivalencia)',
    'inside-buffer': 'En el límite',
    'inside-other-section': 'Otra sección',
    'outside-all-sections': 'Fuera de todo',
    'low-accuracy': 'Impreciso',
    unavailable: 'Sin GPS',
  };

  return (
    <>
      <StatusChip status={isValid ? 'good' : 'warning'}>
        {label[interview.gpsStatus] ?? interview.gpsStatus}
      </StatusChip>
      {interview.detectedFrameSeccion !== null &&
      interview.detectedFrameSeccion !== interview.expectedFrameSeccion ? (
        <div className="faint num">
          {interview.expectedFrameSeccion} → {interview.detectedFrameSeccion}
        </div>
      ) : null}
      {interview.distanceToExpectedMeters !== null && interview.distanceToExpectedMeters > 0 ? (
        <div className="faint num">{Math.round(interview.distanceToExpectedMeters)} m</div>
      ) : null}
      {interview.outOfPolygonReason ? (
        <div className="faint">{interview.outOfPolygonReason}</div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Detalle de una entrevista                            */
/* -------------------------------------------------------------------------- */

/**
 * Las respuestas de una entrevista, agrupadas por bloque.
 *
 * Hasta ahora la única forma de leer lo que contestó alguien era exportar el
 * CSV y buscar la fila. El endpoint ya devolvía todo; faltaba la pantalla.
 *
 * NO muestra los datos de contacto de Q1/Q2: viajan cifrados y se piden
 * aparte con permiso `pii:read`, dejando registro en la bitácora (§11).
 */
function InterviewDetailPanel({
  query,
  onClose,
  canReadPii,
  canRelocate,
}: {
  query: UseQueryResult<InterviewDetail>;
  onClose: () => void;
  canReadPii: boolean;
  canRelocate: boolean;
}) {
  const [contacto, setContacto] = useState<{
    name: string | null;
    phone: string | null;
  } | null>(null);
  const [contactoError, setContactoError] = useState<string | null>(null);
  const [motivoPiloto, setMotivoPiloto] = useState('');
  const [destino, setDestino] = useState('');
  const [motivoMudanza, setMotivoMudanza] = useState('');
  const queryClient = useQueryClient();

  /**
   * Conglomerados a los que se puede mover. Se cargan sólo cuando hay permiso:
   * es una lista de 41 que al resto de los roles no le sirve de nada.
   */
  const clusters = useQuery({
    queryKey: ['clusters'],
    queryFn: () => api.clusters(),
    enabled: canRelocate,
  });

  const mudar = useMutation({
    mutationFn: ({ id, clusterId, reason }: { id: string; clusterId: string; reason: string }) =>
      api.relocateInterview(id, clusterId, reason),
    onSuccess: () => {
      setDestino('');
      setMotivoMudanza('');
      void queryClient.invalidateQueries({ queryKey: ['interview'] });
      void queryClient.invalidateQueries({ queryKey: ['interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      void queryClient.invalidateQueries({ queryKey: ['clusters'] });
    },
  });

  const marcarPiloto = useMutation({
    mutationFn: ({ id, isPilot, reason }: { id: string; isPilot: boolean; reason: string }) =>
      api.setPilot(id, isPilot, reason),
    onSuccess: () => {
      setMotivoPiloto('');
      void queryClient.invalidateQueries({ queryKey: ['interview'] });
      void queryClient.invalidateQueries({ queryKey: ['interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    },
  });

  const pedirContacto = useMutation({
    mutationFn: (id: string) => api.contact(id),
    onSuccess: (r) => {
      setContactoError(null);
      setContacto({ name: r.name, phone: r.phone });
    },
    onError: (e) =>
      setContactoError(e instanceof ApiError ? e.message : 'No se pudo consultar el contacto.'),
  });

  if (query.isLoading) {
    return (
      <div className="panel">
        <div className="spinner" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="panel">
        <div className="callout callout--danger" style={{ marginBottom: 0 }}>
          No se pudo cargar la entrevista.
        </div>
      </div>
    );
  }

  const d = query.data;
  const sinRespuestas = d.transcript.blocks.length === 0 && d.transcript.unmatched.length === 0;

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">
            Entrevista <span className="mono">{d.clientUuid.slice(0, 8)}</span>
          </h2>
          <p className="panel__note">
            {d.cluster.id} · Sección {d.cluster.seccion} · {d.encuestador.code}{' '}
            {d.encuestador.name} ·{' '}
            {new Date(d.startedAt).toLocaleString('es-MX', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {d.durationMinutes !== null ? ` · ${fmtDecimal(d.durationMinutes, 1)} min` : ''}
          </p>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {/* ------------------------- Contexto de captura ------------------- */}

      <div className="row row--wrap" style={{ gap: 20, marginBottom: 16 }}>
        <Tile label="Instrumento" value={d.instrument.version} />
        <Tile label="Estatus" value={d.status} />
        {d.experimentCellId ? <Tile label="Celda X1" value={d.experimentCellId} /> : null}
        {d.household?.eligibleAdults !== null && d.household?.eligibleAdults !== undefined ? (
          <Tile label="Adultos elegibles" value={fmtInt(d.household.eligibleAdults)} />
        ) : null}
        {d.nonSubstantiveRatio !== null ? (
          <Tile label="NS/NC" value={`${Math.round(d.nonSubstantiveRatio * 100)}%`} />
        ) : null}
      </div>

      {/**
       * El GPS se muestra con lo DETECTADO y lo ESPERADO juntos.
       * Verlos por separado obliga a recordar uno de los dos para juzgar si la
       * captura cayó donde debía, que es la pregunta de §9.4.
       */}
      {d.gpsStatus ? (
        <div className={`callout callout--${d.gpsStatus.startsWith('inside') ? 'info' : 'warning'}`}>
          <span className="callout__title">GPS · {d.gpsStatus}</span>
          Detectada: sección {d.detectedFrameSeccion ?? '—'}
          {d.detectedSeccion !== null && d.detectedSeccion !== d.detectedFrameSeccion
            ? ` (cartografía ${d.detectedSeccion}, resuelta por equivalencia)`
            : ''}{' '}
          · esperada: {d.expectedFrameSeccion ?? '—'}
          {d.distanceToExpectedMeters !== null
            ? ` · ${fmtInt(Math.round(d.distanceToExpectedMeters))} m del polígono`
            : ''}
          {d.outOfPolygonReason ? (
            <div style={{ marginTop: 6 }}>
              <strong>Motivo del encuestador:</strong> {d.outOfPolygonReason}
              {d.outOfPolygonNote ? ` — ${d.outOfPolygonNote}` : ''}
            </div>
          ) : null}
        </div>
      ) : null}

      {canRelocate ? (
        <section style={{ marginTop: 16 }}>
          <h3 className="panel__title" style={{ fontSize: 15 }}>
            Mover a otro conglomerado
          </h3>
          <p className="panel__note">
            Hoy cuenta en <strong>{d.cluster.id}</strong> (sección{' '}
            {d.expectedFrameSeccion ?? d.cluster.seccion}). Mover la entrevista reevalúa el GPS
            contra la sección nueva y recalcula las banderas de ubicación; la vivienda viaja con
            ella y el encuestador sigue siendo el mismo.
            {d.detectedFrameSeccion !== null &&
            d.detectedFrameSeccion !== (d.expectedFrameSeccion ?? d.cluster.seccion) ? (
              <>
                {' '}
                El GPS la sitúa en la sección <strong>{d.detectedFrameSeccion}</strong>.
              </>
            ) : null}
          </p>
          <div className="row row--wrap" style={{ gap: 8, alignItems: 'flex-start' }}>
            <select
              className="input"
              style={{ maxWidth: 260 }}
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
            >
              <option value="">Conglomerado destino…</option>
              {(clusters.data ?? [])
                .filter((c) => c.id !== d.cluster.id)
                // Los de la sección que detectó el GPS van primero: es el
                // destino probable cuando el motivo fue un error de asignación.
                .sort((a, b) => {
                  const s = d.detectedFrameSeccion;
                  const pa = a.seccion === s ? 0 : 1;
                  const pb = b.seccion === s ? 0 : 1;
                  return pa - pb || a.id.localeCompare(b.id);
                })
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id} · sección {c.seccion} · est. {c.stratumId}
                    {c.seccion === d.detectedFrameSeccion ? '  ← donde cayó el GPS' : ''}
                  </option>
                ))}
            </select>
            <input
              className="input"
              style={{ maxWidth: 300 }}
              value={motivoMudanza}
              placeholder="Motivo (p. ej. error de asignación)"
              onChange={(e) => setMotivoMudanza(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={!destino || motivoMudanza.trim().length < 4 || mudar.isPending}
              onClick={() =>
                mudar.mutate({ id: d.id, clusterId: destino, reason: motivoMudanza.trim() })
              }
            >
              {mudar.isPending ? 'Moviendo…' : 'Mover'}
            </button>
          </div>
          <p className="faint" style={{ marginTop: 6 }}>
            Cambia a qué celda del diseño cuenta este caso, así que mueve los denominadores de §4.2
            y §6. Queda en la bitácora.
          </p>
          {mudar.isError ? (
            <div className="callout callout--danger" style={{ marginTop: 10, marginBottom: 0 }}>
              {mudar.error instanceof ApiError ? mudar.error.message : 'No se pudo mover.'}
            </div>
          ) : null}
        </section>
      ) : null}

      {d.annulment ? (
        <div className="callout callout--danger">
          <span className="callout__title">Anulación {d.annulment.status}</span>
          {d.annulment.reason}
          {d.annulment.requestedBy ? ` · solicitó ${d.annulment.requestedBy.code}` : ''}
          {d.annulment.approvedBy ? ` · aprobó ${d.annulment.approvedBy.code}` : ''}
          {d.annulment.unilateral ? (
            <div style={{ marginTop: 6 }}>
              <StatusChip status="warning">Resuelta por una sola persona</StatusChip>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---------------------------- Respuestas ------------------------- */}

      {sinRespuestas ? (
        <div className="callout callout--info" style={{ marginBottom: 0 }}>
          Esta entrevista no tiene respuestas registradas. Si está en curso, todavía no se ha
          sincronizado desde el dispositivo.
        </div>
      ) : (
        <>
          {d.transcript.blocks.map((block) => (
            <section key={block.blockId} style={{ marginTop: 18 }}>
              <h3 className="panel__title" style={{ fontSize: 15 }}>
                Bloque {block.blockId} · {block.title}
              </h3>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {block.answers.map((a, i) => (
                      <tr key={`${a.questionCode}-${a.rowCode ?? ''}-${a.rankOrder ?? i}`}>
                        <td style={{ width: 90, verticalAlign: 'top' }}>
                          <span className="mono strong">{a.questionCode}</span>
                          {a.rankOrder ? <div className="faint">#{a.rankOrder}</div> : null}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          {a.rowLabel ? <div className="strong">{a.rowLabel}</div> : null}
                          <div className="faint">{a.questionText ?? '—'}</div>
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          <AnswerValue answer={a} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          {/**
           * Respuestas que la versión aplicada ya no reconoce. Se muestran en
           * lugar de descartarse: son datos levantados de verdad, y
           * esconderlos por un desajuste de catálogo es justo lo contrario de
           * lo que sirve al auditar.
           */}
          {d.transcript.unmatched.length > 0 ? (
            <section style={{ marginTop: 18 }}>
              <h3 className="panel__title" style={{ fontSize: 15 }}>
                Sin pregunta en {d.instrument.version}
              </h3>
              <p className="panel__note">
                Se capturaron con una versión anterior del cuestionario y su pregunta ya no existe.
                Se conservan tal cual.
              </p>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {d.transcript.unmatched.map((a, i) => (
                      <tr key={`${a.questionCode}-${i}`}>
                        <td style={{ width: 90 }}>
                          <span className="mono strong">{a.questionCode}</span>
                        </td>
                        <td>
                          <AnswerValue answer={a} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      )}

      {/* --------------------- Prueba o muestra real --------------------- */}

      <section style={{ marginTop: 20 }}>
        <h3 className="panel__title" style={{ fontSize: 15 }}>
          Clasificación
        </h3>
          <p className="panel__note">
            {d.isPilot
              ? 'Marcada como PRUEBA: no cuenta en el avance hacia la meta, ni en el balanceo del experimento, ni en la cobertura de I6, ni en la exportación de análisis.'
              : 'Cuenta como muestra real: entra en el avance, en el balanceo del experimento X1, en la cobertura de I6 y en la exportación.'}
          </p>
          <div className="row row--wrap" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input
              className="input"
              style={{ maxWidth: 320 }}
              value={motivoPiloto}
              placeholder={d.isPilot ? 'Motivo para devolverla a la muestra' : 'Motivo (p. ej. prueba de capacitación)'}
              onChange={(e) => setMotivoPiloto(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={motivoPiloto.trim().length < 4 || marcarPiloto.isPending}
              onClick={() =>
                marcarPiloto.mutate({ id: d.id, isPilot: !d.isPilot, reason: motivoPiloto.trim() })
              }
            >
              {marcarPiloto.isPending
                ? 'Guardando…'
                : d.isPilot
                  ? 'Devolver a la muestra'
                  : 'Marcar como prueba'}
            </button>
          </div>
        <p className="faint" style={{ marginTop: 6 }}>
          No borra nada: la entrevista y sus respuestas se conservan. Queda en la bitácora.
        </p>
      </section>

      {/* ------------------------ Datos de contacto ---------------------- */}

      <section style={{ marginTop: 20 }}>
        <h3 className="panel__title" style={{ fontSize: 15 }}>
          Datos de contacto (Q1–Q2)
        </h3>
        {!d.contact.hasContact ? (
          <p className="panel__note">
            {d.contact.purgedAt
              ? `Purgados el ${new Date(d.contact.purgedAt).toLocaleDateString('es-MX')} (§11).`
              : 'Esta entrevista no registró datos de contacto.'}
          </p>
        ) : contacto ? (
          <p>
            <strong>{contacto.name ?? '—'}</strong>
            {contacto.phone ? ` · ${contacto.phone}` : ''}
          </p>
        ) : (
          <>
            <p className="panel__note">
              Hay contacto registrado (termina en {d.contact.phoneLast4 ?? '—'}). Está cifrado y
              sólo se descifra al consultarlo.
            </p>
            {canReadPii ? (
              <>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={pedirContacto.isPending}
                  onClick={() => pedirContacto.mutate(d.id)}
                >
                  {pedirContacto.isPending ? 'Consultando…' : 'Ver contacto'}
                </button>
                <span className="faint" style={{ marginLeft: 8 }}>
                  La consulta queda registrada en la bitácora a tu nombre.
                </span>
              </>
            ) : (
              <p className="faint">Requiere el permiso pii:read.</p>
            )}
            {contactoError ? (
              <div className="callout callout--danger" style={{ marginTop: 10, marginBottom: 0 }}>
                {contactoError}
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}

/** Pinta el valor de una respuesta según cómo se haya capturado. */
function AnswerValue({ answer }: { answer: TranscriptAnswer }) {
  const partes: string[] = [];
  if (answer.optionLabel) partes.push(answer.optionLabel);
  if (answer.numericValue !== null) partes.push(String(answer.numericValue));
  if (answer.textValue) partes.push(answer.textValue);
  if (answer.specifyText) partes.push(`(${answer.specifyText})`);

  if (partes.length === 0) return <span className="faint">—</span>;
  return (
    <>
      <span>{partes.join(' · ')}</span>
      {/* NS/NC se marca porque §9.4 vigila su proporción por entrevista. */}
      {answer.isNonSubstantive ? (
        <div>
          <span className="chip">no sustantiva</span>
        </div>
      ) : null}
    </>
  );
}
